import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { Document, Page } from "react-pdf";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import DrawerHeader from "./DrawerHeader";
import { saveAs } from "file-saver";

interface PdfViewerProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  title: string;
  error?: string;
  pdfBlob?: Blob;
  footer?: React.ReactNode;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  open,
  loading,
  onClose,
  title,
  pdfBlob,
  footer,
  error,
}) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const anchor = theme.direction === "rtl" ? "left" : "right";

  const [numPages, setNumPages] = useState<number>();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return "";
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]);

  const fileSize = useMemo(() => {
    if (!pdfBlob) return "";
    const sizeKB = pdfBlob.size / 1024;
    if (sizeKB < 1024) return `${sizeKB.toFixed(2)} KB`;
    return `${(sizeKB / 1024).toFixed(2)} MB`;
  }, [pdfBlob]);

  const handleDownload = () => {
    if (!pdfBlob) return;
    saveAs(pdfBlob, title);
  };

  function onDocumentLoadSuccess({ numPages: nextNumPages }: any): void {
    setNumPages(nextNumPages);
  }

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "80%",
            maxWidth: 800,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DrawerHeader
        title={title}
        isLoading={(!pdfBlob || loading) && !error}
        onClose={onClose}
      />

      <Divider />

      {error && (
        <Alert severity="error" sx={{ my: 3, mx: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 400 }}
        ref={setContainerRef}
      >
        {pdfBlob ? (
          <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (_el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={(containerRef?.clientWidth ?? 800) - 20}
              />
            ))}
          </Document>
        ) : null}

        {(loading || !pdfBlob) && !error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {pdfBlob && !loading && (
        <>
          <Divider />

          <Stack sx={{ p: 1 }} spacing={1} direction="row" alignItems="center">
            {footer}

            {pdfBlob && (
              <Box>
                <Button
                  onClick={handleDownload}
                  color="primary"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  title={t("pdfViewer.downloadWithSize", { size: fileSize })}
                >
                  {t("pdfViewer.download")}
                </Button>
              </Box>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
};
